import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AudioService } from '../../services/audio.service';
import { Subject, takeUntil } from 'rxjs';
import { BaseService } from '../../services/base.service';

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrl: './audio-player.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudioPlayerComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$: Subject<null> = new Subject<null>();

  // ViewChild`s
  @ViewChild('audio', { static: true }) audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('currentTime', { static: true }) currentTimeElRef!: ElementRef<HTMLElement>;
  @ViewChild('duration', { static: true }) durationElRef!: ElementRef<HTMLElement>;
  @ViewChild('progressBar', { static: true }) progressBarRef!: ElementRef<HTMLElement>;
  @ViewChild('volume', { static: true }) volume!: ElementRef<any>;

  // Var`s
  private loadedMetadataListener: any;
  private timeUpdateListener: any;

  public currentIndex!: number;
  public currentVolume: number = 1;

  public isVolumeOff: boolean = false;
  public isSeeking: boolean = false;

  public progress: number = 0;

  constructor(
    public baseService: BaseService,
    public readonly audioService: AudioService,
    private renderer: Renderer2,
  ) {}

  ngOnInit() {
    this.initAudio();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();

    if (this.loadedMetadataListener) this.loadedMetadataListener();
    if (this.timeUpdateListener) this.timeUpdateListener();
  }

  initAudio(): void {
    const audio = this.audioRef.nativeElement;
    const currentTimeElem = this.currentTimeElRef.nativeElement;
    const durationElem = this.durationElRef.nativeElement;
    const progressBar = this.progressBarRef.nativeElement;

    this.loadedMetadataListener = this.renderer.listen(audio, 'loadedmetadata', () => {
      durationElem.textContent = this.formatTime(audio.duration);
    });

    this.timeUpdateListener = this.renderer.listen(audio, 'timeupdate', () => {
      if (!this.isSeeking) {
        currentTimeElem.textContent = this.formatTime(audio.currentTime);
        audio.addEventListener('timeupdate', this.syncProgress.bind(this));
        const progressPercent = (audio.currentTime / audio.duration) * 100;
        if (progressPercent === 100) this.audioService.setIsPlay(false);
        progressBar.style.width = `${progressPercent}%`;
      }
    });

    this.renderer.listen(progressBar, 'click', (event: MouseEvent) => {
      const rect = progressBar.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      audio.currentTime = (offsetX / rect.width) * audio.duration;
    });

    this.currentIndex = this.audioService.audioSource.findIndex((v) => v === this.audioService.currentAudio);
    this.audioRef.nativeElement.volume = this.currentVolume;

    this.audioService.playAudio$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (v: any) => (v ? audio.play() : audio.pause()),
    });
  }

  private formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    seconds = Math.floor(seconds % 60);
    return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  public onChangeAudio(isNext: boolean): void {
    const audio = this.audioRef.nativeElement;

    const changeAudio = () => {
      audio.pause();
      this.audioService.setIsPlay(false);

      audio.src = this.audioService.currentAudio;

      audio.play();
      this.audioService.setIsPlay(true);
    };

    if (isNext && this.currentIndex < this.audioService.audioSource.length - 1) {
      this.audioService.setCurrentAudio(this.audioService.audioSource[this.currentIndex + 1]);
      this.currentIndex = this.audioService.audioSource.findIndex((v) => v === this.audioService.currentAudio);
      changeAudio();
    } else if (!isNext && this.currentIndex > 0) {
      this.audioService.setCurrentAudio(this.audioService.audioSource[this.currentIndex - 1]);
      this.currentIndex = this.audioService.audioSource.findIndex((v) => v === this.audioService.currentAudio);
      changeAudio();
    }
  }

  public onChangePlay(): void {
    const audio = this.audioRef.nativeElement;

    if (audio.paused) {
      audio.play();
      this.audioService.setIsPlay(true);
    } else {
      audio.pause();
      this.audioService.setIsPlay(false);
    }
  }

  public onVolumeChange(event: Event): void {
    this.currentVolume = (event.target as HTMLInputElement).valueAsNumber;
    this.isVolumeOff = this.currentVolume === 0;

    this.audioRef.nativeElement.volume = this.currentVolume;
  }

  public onValueMute(): void {
    if (this.isVolumeOff) {
      if (this.currentVolume === 0) {
        this.audioRef.nativeElement.volume = 1;
        this.volume.nativeElement.value = 1;
      } else {
        this.audioRef.nativeElement.volume = this.currentVolume;
        this.volume.nativeElement.value = this.currentVolume;
      }
    } else {
      this.audioRef.nativeElement.volume = 0;
      this.volume.nativeElement.value = 0;
    }

    this.isVolumeOff = !this.isVolumeOff;
  }

  public checkAudioVolume(): string {
    if (this.isVolumeOff) return 'volume_off';

    const volume: number = this.audioRef?.nativeElement?.volume;
    if (volume > 0 && volume < 0.25) return 'volume_mute';
    if (volume >= 0.25 && volume < 0.75) return 'volume_down';

    return 'volume_up';
  }

  public updateProgress(event: Event): void {
    this.isSeeking = true;

    const audio = this.audioRef.nativeElement;
    const value = (event.target as HTMLInputElement).value;
    const currentTimeElem = this.currentTimeElRef.nativeElement;

    this.progress = Number(value);
    currentTimeElem.textContent = this.formatTime((audio.duration / 100) * Number(value));
  }

  public setProgress(event: Event): void {
    const audio = this.audioRef.nativeElement;
    const value = (event.target as HTMLInputElement).value;

    audio.currentTime = (audio.duration / 100) * Number(value);

    this.isSeeking = false;
  }

  public syncProgress(): void {
    if (!this.isSeeking) {
      const audio = this.audioRef.nativeElement;
      this.progress = (audio.currentTime / audio.duration) * 100;
    }
  }

  public onRewind(isForward: boolean): void {
    const audio = this.audioRef.nativeElement;
    audio.currentTime = isForward ? audio.currentTime + 5 : audio.currentTime - 5;
  }

  public onClearAudio() {
    this.audioService.audioSource = [];
    this.audioService.setCurrentAudio('');
  }
}
